import React, { useEffect, useState } from 'react';
import { useComplianceConfig } from '../../../utility/useComplianceConfig';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Link, useLocation } from 'react-router-dom';
import { ImageComponent } from '../../global/imageComponent';
import { useDispatch } from 'react-redux';
import { getGamedatas } from '../../actions/gameSettings';
import SportIcon from '../../common/SportIcon';
import {
  boardSportFromPath,
  favoritesBoardPath,
  sportBoardPath,
} from '../../../constants/routes';
import { isFavoritesBoardFilter } from '../../../utility/favoritesRecents';

/**
 * Where a slider item leads. Board items carry a `sportKey` (a sport, the
 * all-sports board, or the live board) and are routed by it; Favorites is a
 * board filter on all-sports; other fixed pages (call / active entries) use
 * `navLink`. The label is display text only — on DTS it is reworded, so it
 * must never be used as a route or an id.
 */
const linkFor = (item) => {
  if (item.boardFilter === "favorites") {
    return favoritesBoardPath();
  }
  if (item.sportKey != null) {
    return {
      pathname: sportBoardPath(item.sportKey),
      // Drop Favorites filter when switching to a normal sport board.
      search: "",
      state: { selectedSport: item.sportKey },
    };
  }
  return item.navLink;
};

const keyOf = (item) =>
  item.boardFilter ?? item.sportKey ?? item.navLink ?? item.labelText;

export default function AllSportsSlider({ data = [] }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const currentSport = boardSportFromPath(location.pathname);

  // Re-render when admin vocabulary loads so the fixed items, whose labels come
  // from `data`, pick up the configured wording.
  useComplianceConfig();

  // Only the fetched sports live in state. The fixed items are read from the
  // `data` prop on every render, so their labels are always current — copying
  // them into state at mount would freeze the wording from before site
  // settings loaded.
  const [fetchedSports, setFetchedSports] = useState([]);

  const fetchGameData = async () => {
    try {
      const { status, result } = await getGamedatas(dispatch);
      if (status === true && Array.isArray(result)) {
        setFetchedSports(
          result.map((sport, index) => {
            const name = sport._id || sport.name || `Sport ${index + 1}`;
            return {
              sportKey: name,
              labelText: name,
              iconUrl: sport.iconUrl || null,
              count: sport.count || 0,
            };
          })
        );
      }
    } catch (err) {
      console.error("Error fetching sports data:", err);
    }
  };

  useEffect(() => {
    fetchGameData();
  }, []);

  // Fixed items first, then fetched sports; a fetched sport never duplicates a
  // fixed item.
  const fixedKeys = new Set(data.map(keyOf));
  const sliderData = [
    ...data,
    ...fetchedSports.filter((sport) => !fixedKeys.has(keyOf(sport))),
  ];

  const favoritesFilterOn = isFavoritesBoardFilter(location.search);

  const isActive = (item) => {
    if (item.boardFilter === "favorites") {
      return favoritesFilterOn;
    }
    if (favoritesFilterOn) {
      return false;
    }
    if (item.sportKey != null) {
      return currentSport === item.sportKey;
    }
    return location.pathname === item.navLink;
  };

  return (
    <div className='allSportsSliderWrapper'>
      <button className='sliderCustomBtn sliderLeftBtn'>
        <span><IoIosArrowBack /></span>
      </button>

      <Swiper
        modules={[Navigation]}
        spaceBetween={10}
        slidesPerView={"auto"}
        className='allSportsSwiper'
        navigation={{
          prevEl: '.sliderLeftBtn',
          nextEl: '.sliderRightBtn'
        }}
      >
        {sliderData.map((item) => {
          return (
            <SwiperSlide className='allSportsSwiperSlide' key={keyOf(item)}>
              <Link
                to={linkFor(item)}
                className={`allSportsSliderContent ${isActive(item) ? "active" : ""}`}
              >
                <div className='imgWrapper'>
                  {item.iconSrc ? (
                    <ImageComponent staticImgSrc={item.iconSrc} />
                  ) : (
                    <SportIcon
                      sportName={item.labelText}
                      iconUrl={item.iconUrl}
                      size={28}
                    />
                  )}
                  {item.count > 0 && <span className="liveBage2">{item.count}</span>}
                </div>
                <h3 className='fs13'>{item.labelText}</h3>
              </Link>
            </SwiperSlide>
          );
        })}

      </Swiper>

      <button className='sliderCustomBtn sliderRightBtn'>
        <span><IoIosArrowForward /></span>
      </button>
    </div>
  );
}
